import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, TemplateRef } from "@angular/core";

declare const bootstrap: any;
type ConfirmDialogIcon = "trash" | "warning" | "success" | "error";

const STATIC_MODAL_OPTIONS = {
  backdrop: "static",
  keyboard: false,
};
const CLOSE_FALLBACK_MS = 500;

@Component({
  selector: "app-confirm-dialog",
  templateUrl: "./confirm-dialog.component.html",
  styleUrl: "./confirm-dialog.component.scss",
  imports: [CommonModule],
})
export class ConfirmDialogComponent {
  @Input() modalId = "confirmDialog";
  @Input() title = "ยืนยันการลบรายการ";
  @Input() message = "คุณต้องการลบรายการนี้ใช่หรือไม่ ?";
  @Input() cancelText = "ยกเลิก";
  @Input() confirmText = "ยืนยัน";
  @Input() icon: ConfirmDialogIcon = "trash";
  @Input() isProcessing = false;
  @Input() showCancelButton = true;
  @Input() stackAboveModal = false;
  @Input() containerClass = "";
  @Input() contentClass = "";
  @Input() contentTemplate?: TemplateRef<{ $implicit: ConfirmDialogComponent }>;

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  readonly contentTemplateContext = { $implicit: this };

  open(): void {
    const modalEl = document.getElementById(this.modalId);

    if (modalEl == null) {
      return;
    }

    bootstrap.Modal.getOrCreateInstance(modalEl, STATIC_MODAL_OPTIONS).show();
  }

  close(): void {
    const modalEl = document.getElementById(this.modalId);

    if (modalEl == null) {
      return;
    }

    this.blurFocusedElementInside(modalEl);

    const shouldRestoreModalOpen = this.stackAboveModal && this.hasAnotherOpenModal(modalEl);
    let completed = false;
    const complete = () => {
      if (completed) {
        return;
      }

      completed = true;
    };

    if (shouldRestoreModalOpen) {
      modalEl.addEventListener("hidden.bs.modal", () => {
        complete();
        queueMicrotask(() => {
          if (this.hasAnotherOpenModal(modalEl)) {
            document.body.classList.add("modal-open");
          }
        });
      }, { once: true });
    } else {
      modalEl.addEventListener("hidden.bs.modal", complete, { once: true });
    }

    bootstrap.Modal.getInstance(modalEl)?.hide();
    setTimeout(() => {
      if (completed) {
        return;
      }

      this.forceHideModalElement(modalEl);
      complete();
    }, CLOSE_FALLBACK_MS);
  }

  onCancel(): void {
    this.cancelled.emit();
    this.close();
  }

  onConfirm(): void {
    this.confirmed.emit();
  }

  private hasAnotherOpenModal(modalEl: HTMLElement): boolean {
    return Array.from(document.querySelectorAll<HTMLElement>(".modal.show"))
      .some((openModal) => openModal !== modalEl);
  }

  private blurFocusedElementInside(modalEl: HTMLElement): void {
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && modalEl.contains(activeElement)) {
      activeElement.blur();
    }
  }

  private forceHideModalElement(modalEl: HTMLElement): void {
    modalEl.classList.remove("show");
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
    modalEl.removeAttribute("role");
    modalEl.style.display = "none";

    if (this.hasAnotherOpenModal(modalEl)) {
      document.body.classList.add("modal-open");
      return;
    }

    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
    document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
  }
}
